import { tokenState } from '../state/apolloVars';
import { clearSession, isInvalidGrant } from './authFlowLink';
import { getHeaders } from './fetchHeaders';
import { refetchTokenSerially } from './refetchTokenSerially';
import * as settings from '../state/preferences';
import {
  analyticsService,
  POSTHOG_EVENT_ACTIONS,
  POSTHOG_EVENT_DATA,
} from '../analytics/AnalyticsService';

type JSONValue = string | number | boolean | JSONObject | JSONArray;
interface JSONObject {
  [x: string]: JSONValue;
}
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface JSONArray extends Array<JSONValue> {}

async function doFetch(url: string, input: JSONObject) {
  const body = JSON.stringify(input);
  const headers = await getHeaders();
  return fetch(url, {
    method: 'POST',
    body: body,
    headers: headers,
  });
}

async function refreshToken(): Promise<boolean> {
  try {
    await refetchTokenSerially();
    return true;
  } catch (error) {
    console.error('FAIL TO REFRESH USER TOKEN', error);
    if (isInvalidGrant(error)) {
      // Clear in-memory state first so the UI reacts immediately even if the persisted write below fails.
      tokenState({});
      try {
        await clearSession();
      } catch (clearError) {
        console.error('FAIL TO CLEAR USER SESSION', clearError);
      }
    }
    return false;
  } finally {
    try {
      const session = await settings.getSession();
      analyticsService.capture(POSTHOG_EVENT_ACTIONS.TOKEN_REFRESH_OR_EXPIRED, {
        orgId: session?.currentOrganization?.id,
        orgName: session?.currentOrganization?.name,
      } as POSTHOG_EVENT_DATA);
    } catch (analyticsError) {
      console.error('FAIL TO CAPTURE TOKEN REFRESH ANALYTICS', analyticsError);
    }
  }
}

async function refreshTokenAndRetry(
  url: string,
  input: JSONObject,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  const refreshed = await refreshToken();
  if (!refreshed) return {};
  return makeRequest(url, input, true);
}

export async function makeRequest(
  url: string,
  input: JSONObject,
  isRetry = false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<any> {
  let response: Response;
  try {
    response = await doFetch(url, input);
  } catch (e) {
    console.log(e);
    // A rejection doesn't prove the server never processed the request, so refresh the session but don't replay it.
    if (!isRetry && (tokenState().expiredDate as number) < Date.now()) {
      await refreshToken();
    }
    return {};
  }

  if (response.status === 401) {
    if (isRetry) return {};
    return refreshTokenAndRetry(url, input);
  }
  if (!response.ok) {
    console.log(`Request to ${url} failed with status ${response.status}`);
    return {};
  }
  try {
    return await response.json();
  } catch (e) {
    console.log(e);
    return {};
  }
}
